import { useEffect, useMemo, useState } from "react";
import { Checkbox, Flex, Form, Select, Typography } from "antd";
import { MdOutlineArrowOutward } from "react-icons/md";
import FormInput from "./FormInput";
import { useApiCall } from "../../services/api";
import { useSelector } from "react-redux";
import { useGlobalModal } from "../../context/SuccessModalProvider";
import { formRules, idTypeEnums, modificationTitlesTags } from "../../constant";
import { useLocation } from "react-router-dom";
import PrimaryButton from "./PrimaryButton";
import { usePendingModification } from "../../pages/Profile";
import OtpModal from "../models/OtpModal";

const { Title } = Typography;

const SalesForm = ({
  formData,
  runDefaultHandleSubmit = true,
  handleSubmit,
  closeModal,
}) => {
  const { loading, postApi } = useApiCall();
  const location = useLocation();
  const isOwnProfile = [
    "/sales/profile",
    "/admin/profile",
    "/profile",
  ].includes(location.pathname);

  // A memoized key that changes when formData's JSON string changes.
  // This forces a re-render of the entire form.
  const formKey = useMemo(() => {
    return JSON.stringify(formData);
  }, [formData]);

  const user_id = useSelector((state) => state.auth.user_id);

  const onFinish = async (values) => {
    if (runDefaultHandleSubmit) {
      const apiRoute = `user/update-profile`;

      const body = isOwnProfile ? { user_id } : { user_id: formData.user_id };

      try {
        const response = await postApi(
          apiRoute,
          {
            designation: formData.designation,
            email: formData.email,
            phone: formData.phone,
            role_id: formData.role_id,
            ...values,
            ...body,
          },
          true,
        );
        if (response?.status) {
          closeModal(values);
        }
      } catch (error) {
        console.log("Error", error);
      }
    }
    if (handleSubmit) {
      handleSubmit(values);
    }
  };

  return (
    <>
      <Form
        // The most reliable solution: use a key to force a re-mount.
        key={formKey}
        className="mt-6"
        name="address-form"
        layout="vertical"
        autoComplete="off"
        onFinish={onFinish}
        initialValues={formData}
      >
        <FormInput
          label="Employee Name"
          name="full_name"
          rules={formRules.required}
        />
        <FormInput
          label="Designation"
          name="designation"
          rules={formRules.required}
        />
        <FormInput
          label="Display Email Address"
          name="display_email_address"
          rules={formRules.email}
        />
        <FormInput
          label="Display Contact No."
          name="display_contact_no"
          rules={formRules.phone}
          maxLength={10}
        />
        <Flex justify="space-between">
          <Form.Item
            label="ID Type"
            name="id_type"
            rules={formRules.required}
            style={{ flex: 1 }}
          >
            <Select size="large" style={{ width: "90%", height: "47.6px" }}>
              {idTypeEnums.user.map((item) => (
                <Select.Option key={item} value={item}>
                  {item}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <FormInput
            label="ID Number"
            name="id_number"
            rules={formRules.required}
          />
        </Flex>
        <Flex justify="space-between">
          <FormInput label="Zone" name="zone" style={{ width: "48%" }} />
          <FormInput label="Area" name="area" style={{ width: "48%" }} />
        </Flex>
        <FormInput label="Remarks" name="remark" />

        <div className="w-full mt-4 flex justify-center">
          <PrimaryButton
            className="!mr-[35px]"
            type="submit"
            text="Save and Proceed"
            loading={loading}
          />
        </div>
      </Form>
    </>
  );
};

const ClientForm = ({
  formData,
  runDefaultHandleSubmit = true,
  handleSubmit,
  closeModal,
  onOtpModalToggle,
}) => {
  const { pending_modification } = usePendingModification() || {};
  const { showPopup } = useGlobalModal();
  const [step, setStep] = useState(1);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpFormData, setOtpFormData] = useState({});
  const [verifyOtp, setVerifyOtp] = useState({ email: true, phone: true });
  const { routeType } = usePendingModification() || {};
  const location = useLocation();

  const mergedFormData = useMemo(() => {
    return { ...formData, ...otpFormData };
  }, [formData, otpFormData]);

  // A memoized key that changes when formData's JSON string changes.
  // This forces a re-render of the entire form.
  const formKey = useMemo(() => {
    return JSON.stringify(mergedFormData);
  }, [mergedFormData]);

  const { loading, postApi } = useApiCall();
  const { full_name, user_id, is_sales_manager, is_admin, is_customer } =
    useSelector((state) => state.auth);

  const sendOtp = async (type, values, showResponse = false) => {
    const data = { [type]: values[type], deviceType: "Web" };
    if (type === "phone") {
      data.email = values.email;
    }

    const apiRoute = `sales/employee/signup-send-otp-${type}`;
    const res = await postApi(apiRoute, data, showResponse);
    return !!res?.status;
  };

  const handleResendOtp = async (type = "email", showResponse = false) => {
    return sendOtp(type, otpFormData, showResponse);
  };

  const validateDuplicateCustomer = async (values) => {
    const checkExisting = await postApi("customer/check-duplicate-customer", {
      phone: values.phone,
      email: values.email,
      customer_id: formData.customer_id,
    });

    if (!checkExisting.status) {
      showPopup({
        title: null,
        content: (
          <span className="custom-modal-title">{checkExisting.message}</span>
        ),
        onOk: () => {
          console.log("Confirmed!");
        },
        className: "custom-success-modal",
      });
      return false;
    }

    return true;
  };

  const submitProfile = async (values, { skipDuplicateCheck = false } = {}) => {
    if (runDefaultHandleSubmit) {
      if (!skipDuplicateCheck) {
        const isValid = await validateDuplicateCustomer(values);
        if (!isValid) return false;
      }

      const { bool, customer_request_id, title, tags, customer_update_id } =
        pending_modification || {};
      const apiRoute =
        is_sales_manager || is_admin || is_customer
          ? `customer/update-profile`
          : bool && customer_update_id
            ? `sales/customers/update-modification-request`
            : `sales/customers/add`;

      const updatedFormData = new FormData();

      const setField = (key, value) => {
        if (value !== undefined && value !== null && value !== "") {
          updatedFormData.set(key, value);
        }
      };

      if (bool && customer_update_id) {
        setField("customer_update_id", customer_update_id);
      } else {
        setField("request_id", customer_request_id);
      }

      setField("title", title || modificationTitlesTags.updateTitle);
      setField(
        "tags",
        tags ||
          modificationTitlesTags.updateTags(full_name, formData.old_comp_name),
      );

      Object.keys(formData).forEach((key) => {
        if (
          [
            "customer_id",
            "user_id",
            "address_line1",
            "address_line2",
            "state",
            "city",
            "post_code",
          ].includes(key)
        ) {
          setField(key, formData[key]);
        }
      });

      Object.keys(values).forEach((key) => {
        setField(key, values[key]);
      });

      setField("updated_by", full_name);
      setField("updated_by_id", user_id);

      try {
        const response = await postApi(apiRoute, updatedFormData, true);
        if (response?.status) {
          closeModal();
        }
        if (!response?.status) return false;
      } catch (error) {
        console.log("Error", error);
        return false;
      }
    }

    if (handleSubmit) {
      await handleSubmit(values);
    }

    return true;
  };

  const onFinish = async (values) => {
    const isValid = await validateDuplicateCustomer(values);
    if (!isValid) return;

    const emailChanged =
      (values.email || "") !== (formData?.email?.trim() || "");
    const phoneChanged =
      (values.phone || "") !== (formData?.phone?.trim() || "");
    const isOwnProfile = [
      "/sales/profile",
      "/admin/profile",
      "/profile",
    ].includes(location.pathname);
    const requiresOtp = isOwnProfile && (emailChanged || phoneChanged);

    if (requiresOtp) {
      setOtpFormData(values);
      setVerifyOtp({ email: emailChanged, phone: phoneChanged });

      const sentEmail = emailChanged ? await sendOtp("email", values) : true;
      const sentPhone = phoneChanged ? await sendOtp("phone", values) : true;

      if (!sentEmail || !sentPhone) {
        showPopup({
          title: null,
          content: (
            <span className="custom-modal-title">
              Failed to send OTP to the provided {!sentEmail ? "email" : ""}
              {!sentPhone ? (!sentEmail ? " and phone" : "phone") : ""}.
            </span>
          ),
          onOk: () => console.log("Confirmed!"),
          className: "custom-success-modal",
        });
        return;
      }

      showPopup({
        title: null,
        content: (
          <span className="custom-modal-title">
            Please enter the OTP sent to the provided{" "}
            {emailChanged && phoneChanged
              ? "email and phone"
              : emailChanged
                ? "email"
                : "phone"}
            .
          </span>
        ),
        onOk: () => {
          setStep((prev) => prev + 1);
          setShowOtpModal(true);
        },
        className: "custom-success-modal",
      });
      return;
    }

    await submitProfile(values, { skipDuplicateCheck: true });
  };

  useEffect(() => {
    onOtpModalToggle?.(showOtpModal);
  }, [showOtpModal, onOtpModalToggle]);

  if (showOtpModal) {
    return (
      <OtpModal
        formType="client"
        is_customer={true}
        step={step}
        formData={otpFormData}
        handleResendOtp={handleResendOtp}
        handleFinish={async () => {
          const res = await submitProfile(otpFormData, {
            skipDuplicateCheck: true,
          });
          if (res) {
            setShowOtpModal(false);
          }
        }}
        title={"OTP Verification"}
        verifyEmailOtp={verifyOtp.email}
        verifyPhoneOtp={verifyOtp.phone}
        onPrevious={() => {
          setStep((prev) => prev - 1);
          setShowOtpModal(false);
        }}
      />
    );
  }

  return (
    <>
      <Form
        // The most reliable solution: use a key to force a re-mount.
        key={formKey}
        className="mt-6"
        name="address-form"
        layout="vertical"
        autoComplete="off"
        onFinish={onFinish}
        initialValues={mergedFormData}
      >
        <FormInput
          label="Company Name"
          name="company_name"
          rules={formRules.required}
        />
        <FormInput
          label="Contact Person"
          name="contact_person"
          rules={formRules.required}
        />
        <FormInput label="Email Address" name="email" rules={formRules.email} />
        <FormInput
          label="Phone"
          name="phone"
          rules={formRules.phone}
          maxLength={10}
        />
        <Flex justify="space-between">
          <Form.Item
            label="ID Type"
            name="id_type"
            rules={formRules.required}
            style={{ flex: 1 }}
          >
            <Select style={{ width: "90%", height: "47.6px" }}>
              {idTypeEnums.client.map((item) => (
                <Select.Option key={item} value={item}>
                  {item}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <FormInput
            label="ID Number"
            name="id_no"
            rules={formRules.required}
          />
        </Flex>
        <Flex justify="space-between">
          <FormInput label="Zone" name="zone" style={{ width: "48%" }} />
          <FormInput label="Area" name="area" style={{ width: "48%" }} />
        </Flex>
        <FormInput label="Agent Name" name="agent_name" />
        <FormInput label="Remarks" name="agent_remark" />
        <div className="w-full mt-4 flex justify-center">
          <PrimaryButton
            className="!mr-[40px]"
            type="submit"
            text="Save and Proceed"
            loading={loading}
          />
        </div>
      </Form>
    </>
  );
};

const CompanyInfo = ({
  title,
  formData,
  handleSubmit,
  runDefaultHandleSubmit = true,
  formType = "sales",
  closeModal,
}) => {
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  // A memoized key that changes when formData's JSON string changes.
  // This forces a re-render of the entire form.
  const formKey = useMemo(() => {
    return JSON.stringify(formData);
  }, [formData]);

  return (
    <>
      {!isOtpModalOpen && (
        <Title className="input-css font-abhaya" level={2}>
          {title || (formData.type === "add" ? "Add Address" : "Edit Address")}
        </Title>
      )}
      {formType === "client" ? (
        <ClientForm
          formData={{ ...formData, old_comp_name: formData.company_name }}
          runDefaultHandleSubmit={runDefaultHandleSubmit}
          handleSubmit={handleSubmit}
          closeModal={closeModal}
          onOtpModalToggle={setIsOtpModalOpen}
        />
      ) : (
        <SalesForm
          formData={formData}
          runDefaultHandleSubmit={runDefaultHandleSubmit}
          handleSubmit={handleSubmit}
          closeModal={closeModal}
        />
      )}
    </>
  );
};

export default CompanyInfo;
